import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppContext from "../Utils/appContext";
import { getCart, normalizeProductId } from "../Utils/cart-service";
import { vibrate } from "../Utils/util-service";
import "./FloatingCartBar.css";

const MAX_THUMBNAILS = 3;
const PLACEHOLDER_IMAGE = "assets/images/products/1000.jpg";
const HIDDEN_ON_PATHS = ["/cart", "/delivery", "/order-success"];
const SHOW_DELAY_MS = 400;

/** Most recently added distinct products first (cart lines are appended). */
const getThumbnails = (cart) => {
  const seen = new Set();
  const thumbnails = [];
  for (let i = cart.length - 1; i >= 0; i--) {
    const item = cart[i];
    const key = normalizeProductId(item?.id) || `line-${i}`;
    if (seen.has(key)) continue;
    seen.add(key);
    thumbnails.push({
      key,
      title: item?.title || "",
      src: item?.images?.[0]?.url || PLACEHOLDER_IMAGE,
    });
    if (thumbnails.length === MAX_THUMBNAILS) break;
  }
  return thumbnails;
};

const handleImageError = (event) => {
  const img = event.currentTarget;
  if (img.dataset.fallback) {
    img.style.visibility = "hidden";
    return;
  }
  img.dataset.fallback = "true";
  img.src = PLACEHOLDER_IMAGE;
};

/** True while the window is scrolling; resets SHOW_DELAY_MS after the last scroll event. */
const useIsScrolling = (enabled) => {
  const [isScrolling, setIsScrolling] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return undefined;
    let timer;
    let scrolling = false;
    const onScroll = () => {
      if (!scrolling) {
        scrolling = true;
        setIsScrolling(true);
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        scrolling = false;
        setIsScrolling(false);
      }, SHOW_DELAY_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
      setIsScrolling(false);
    };
  }, [enabled]);

  return isScrolling;
};

function FloatingCartBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { cartLength, cartVersion } = React.useContext(AppContext);

  const cart = React.useMemo(
    () => getCart(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartLength, cartVersion, pathname],
  );

  const itemCount = cart.length;  const isActive = itemCount > 0 && !HIDDEN_ON_PATHS.includes(pathname);
  const isScrolling = useIsScrolling(isActive);

  if (!isActive) return null;

  const thumbnails = getThumbnails(cart);
  const countLabel = `${itemCount} ${itemCount === 1 ? "item" : "items"}`;

  const goToCart = () => {
    vibrate(20);
    navigate("/cart");
  };

  return (
    <>
      <div className="floating-cart-spacer" aria-hidden="true" />
      <div
        className={`floating-cart-wrapper${isScrolling ? " is-scrolling" : ""}`}
      >
        <button
          type="button"
          className="floating-cart-bar"
          onClick={goToCart}
          aria-label={`View cart, ${countLabel}`}
        >
          <span className="floating-cart-thumbs" aria-hidden="true">
            {thumbnails
              .slice()
              .reverse()
              .map((thumb) => (
                <span className="floating-cart-thumb" key={thumb.key}>
                  <img
                    src={thumb.src}
                    alt=""
                    loading="lazy"
                    onError={handleImageError}
                  />
                </span>
              ))}
          </span>
          <span className="floating-cart-text" aria-hidden="true">
            <span className="floating-cart-title">View cart</span>
            <span className="floating-cart-count">{countLabel}</span>
          </span>
          <span className="floating-cart-arrow" aria-hidden="true">
            <i className="iconly-Arrow-Right-2 icli"></i>
          </span>
        </button>
      </div>
    </>
  );
}

export default FloatingCartBar;
